import { DayOfWeek } from './types.js';
/**
 * Weekdays array (Monday through Friday)
 */
export const WEEKDAYS = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
];
/**
 * Weekends array (Saturday and Sunday)
 */
export const WEEKENDS = [DayOfWeek.Saturday, DayOfWeek.Sunday];
/**
 * All days of the week starting from Monday
 * Use this for consistent day ordering across the application
 */
export const ALL_DAYS_MONDAY_FIRST = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday,
    DayOfWeek.Sunday,
];
/**
 * Formats a time string to remove ":00" when minutes are zero and leading zeros from hours
 * @param time - Time string like "9:00 AM", "07:00 AM", or "11:45 PM"
 * @example formatTime("9:00 AM") // "9 AM"
 * @example formatTime("07:00 AM") // "7 AM"
 * @example formatTime("07:30 AM") // "7:30 AM"
 * @example formatTime("11:45 PM") // "11:45 PM"
 */
export function formatTime(time) {
    // Remove leading zero from single-digit hours (e.g., "07:00 AM" -> "7:00 AM")
    let formatted = time.replace(/^0(\d)/, '$1');
    // Then remove :00 for zero minutes (e.g., "7:00 AM" -> "7 AM")
    formatted = formatted.replace(':00', '');
    return formatted;
}
/**
 * Formats an array of time intervals into a display string
 * @param intervals - Array of time intervals with start and end times
 * @example formatTimeIntervals([{ start: '9:00 AM', end: '5:00 PM' }]) // "9 AM - 5 PM"
 * @example formatTimeIntervals([{ start: '9:15 AM', end: '5:30 PM' }]) // "9:15 AM - 5:30 PM"
 */
export function formatTimeIntervals(intervals) {
    if (intervals.length === 0)
        return '';
    return intervals
        .map((i) => `${formatTime(i.start)} - ${formatTime(i.end)}`)
        .join(', ');
}
/**
 * Creates a schedule key for comparison to determine if two schedules are identical
 * @param daySchedule - Day schedule object or null/undefined
 */
export function getScheduleKey(daySchedule) {
    if (!daySchedule)
        return 'undefined';
    return `${daySchedule.open}-${JSON.stringify(daySchedule.hours)}`;
}
/**
 * Checks if all schedules in an array are identical
 * @param schedules - Array of day schedules to compare
 */
export function areAllSchedulesSame(schedules) {
    const first = schedules[0];
    if (!first)
        return false;
    return schedules.every((schedule) => schedule?.open === first.open &&
        JSON.stringify(schedule?.hours) === JSON.stringify(first.hours));
}
/**
 * Checks if a group of days represents all weekdays (Mon-Fri)
 * @param days - Array of days to check
 */
export function isWeekdaysGroup(days) {
    return days.length === 5 && WEEKDAYS.every((day) => days.includes(day));
}
/**
 * Checks if a group of days represents weekends (Sat-Sun)
 * @param days - Array of days to check
 */
export function isWeekendsGroup(days) {
    return (days.length === 2 &&
        days.includes(DayOfWeek.Saturday) &&
        days.includes(DayOfWeek.Sunday));
}
/**
 * Groups consecutive days with the same schedule
 * @param days - Array of days to process
 * @param getScheduleForDay - Function to get the schedule for a given day
 * @returns Array of grouped day entries with their schedule keys
 */
export function groupConsecutiveDays(days, getScheduleForDay) {
    return days.reduce((groups, day) => {
        const daySchedule = getScheduleForDay(day);
        if (!daySchedule)
            return groups;
        const scheduleKey = getScheduleKey(daySchedule);
        const lastGroup = groups[groups.length - 1];
        // If last group has same schedule, add to it (consecutive grouping)
        if (lastGroup?.scheduleKey === scheduleKey) {
            lastGroup.days.push(day);
        }
        else {
            // Start new group
            groups.push({ days: [day], scheduleKey });
        }
        return groups;
    }, []);
}
//# sourceMappingURL=formatUtils.js.map